import { useState, useEffect } from "react";
import { functions } from "../firebaseConfig";
import { httpsCallable } from "firebase/functions";

import Header from "../components/Header";
import { useDesignVersion } from "../DesignVersionContext";
import PartnerV2 from "./PartnerV2";
import common from "../style/screens/Common.module.css";
import style from "../style/screens/Partner.module.css";
import ErrMsg from "../ErrMsg";
import { BlockButton, ListButton } from "../components/Buttons";
import { BlockInput } from "../components/Inputs";
import { PartnerType, usePartner } from "../PartnerContext";

const Partner = () => {
    const { version } = useDesignVersion();
    const [addPartner, setAddPartner] = useState<boolean>(false);

    if (version === "v2") {
        return <PartnerV2 />;
    }

    return (
        <>
            <Header />
            <div className={common["container"]}>
                <div className={common["wrapper"]}>
                    <h4 className={common["text--title"]}>파트너사 관리</h4>
                    <p className={common["text--copy"]}>파트너사 정보를 변경하거나 삭제할 수 있습니다.</p>
                    <button className={style["button--add-partner"]} onClick={() => setAddPartner(true)}>
                        파트너사 추가하기
                    </button>
                    <PartnerTable />
                </div>
            </div>

            {addPartner && <AddPartner close={() => setAddPartner(false)} />}
        </>
    );
};

const AddPartner = ({ close }: { close: () => void }) => {
    const [partnerId, setPartnerId] = useState<string>("");
    const [partnerIdError, setPartnerIdError] = useState<string>("");

    useEffect(() => {
        if (partnerId && partnerId.length < 3) {
            setPartnerIdError(ErrMsg["auth/partnerId-invalid"]);
        } else {
            setPartnerIdError("");
        }
    }, [partnerId]);

    const [partnerName, setPartnerName] = useState<string>("");
    const [partnerNameError, setPartnerNameError] = useState<string>("");

    useEffect(() => {
        if (partnerName && partnerName.length < 2) {
            setPartnerNameError(ErrMsg["auth/partnerName-invalid"]);
        } else {
            setPartnerNameError("");
        }
    }, [partnerName]);

    const [name, setName] = useState<string>("");
    const [nameError, setNameError] = useState<string>("");

    useEffect(() => {
        if (name && name.length < 2) {
            setNameError(ErrMsg["auth/name-invalid"]);
        } else {
            setNameError("");
        }
    }, [name]);

    const [email, setEmail] = useState<string>("");
    const [emailError, setEmailError] = useState<string>("");

    useEffect(() => {
        if (email && !/^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/.test(email)) {
            setEmailError(ErrMsg["auth/email-invalid"]);
        } else {
            setEmailError("");
        }
    }, [email]);

    const [password, setPassword] = useState<string>("");
    const [passwordError, setPasswordError] = useState<string>("");

    useEffect(() => {
        if (password && password.length < 6) {
            setPasswordError(ErrMsg["auth/password-invalid"]);
        } else {
            setPasswordError("");
        }
    }, [password]);

    const [confirm, setConfirm] = useState<string>("");
    const [confirmError, setConfirmError] = useState<string>("");

    useEffect(() => {
        if (confirm && confirm !== password) {
            setConfirmError(ErrMsg["auth/password-not-match"]);
        } else {
            setConfirmError("");
        }
    }, [confirm, password]);

    const [loading, setLoading] = useState<boolean>(false);

    const createPartner = async () => {
        if (nameError || emailError || passwordError || confirmError) {
            return;
        }

        if (!partnerId) {
            setPartnerIdError(ErrMsg["auth/partnerId-empty"]);
            return;
        } else if (!partnerName) {
            setPartnerNameError(ErrMsg["auth/partnerName-empty"]);
            return;
        } else if (!name) {
            setNameError(ErrMsg["auth/name-empty"]);
            return;
        } else if (!email) {
            setEmailError(ErrMsg["auth/email-empty"]);
            return;
        } else if (!password) {
            setPasswordError(ErrMsg["auth/password-empty"]);
            return;
        } else if (!confirm) {
            setConfirmError(ErrMsg["auth/password-empty"]);
            return;
        }

        setLoading(true);

        return httpsCallable(
            functions,
            "partnerCreate"
        )({
            partner: {
                partnerId: partnerId,
                name: partnerName,
            },
            admin: {
                name: name,
                type: "GROUP_ADMIN",
                email: email,
                password: password,
                confirm: confirm,
                partnerId: partnerId,
            },
        })
            .then((result) => {
                setLoading(false);

                const data = result.data as {
                    code?: string;
                    message?: string;
                };

                if (data.code) {
                    hide();
                }
            })
            .catch((err) => {
                console.error(err.message);
                setLoading(false);
                setLoading(false);
                switch (err.message) {
                    case "auth/name-invalid":
                    case "auth/name-weak":
                        setNameError(ErrMsg[err.message]);
                        break;
                    case "auth/partnerId-invalid":
                    case "auth/partnerId-already-in-use":
                    case "auth/partnerId-weak":
                        setPartnerIdError(ErrMsg[err.message]);
                        break;
                    case "auth/partnerName-invalid":
                    case "auth/partnerName-already-in-use":
                        setPartnerNameError(ErrMsg[err.message]);
                        break;
                    case "auth/email-invalid":
                    case "auth/email-already-in-use":
                        setEmailError(ErrMsg[err.message]);
                        break;
                    case "auth/password-invalid":
                    case "auth/password-weak":
                        setPasswordError(ErrMsg[err.message]);
                        break;
                    case "auth/password-not-match":
                        setConfirmError(ErrMsg[err.message]);
                        break;
                    case "auth/not-super-admin":
                        alert("최고 관리자만 생성이 가능합니다");
                        break;
                }

                return false;
            });
    };

    const [active, setActive] = useState<boolean>(false);
    useEffect(() => {
        setActive(true);
    }, []);
    const hide = () => {
        setActive(false);
        setTimeout(() => {
            close();
        }, 520);
    };
    return (
        <div className={[common["container--overlay"], active && common["container--overlay--active"]].join(" ")}>
            <h4 className={[common["text--title"], common["text--title--overlay"]].join(" ")}>파트너사 추가</h4>

            <div className={[common["unit--overlay"], active && common["unit--overlay--active"]].join(" ")}>
                <div className={common["wrapper--overlay"]}>
                    <h4 className={common["text--overlay"]}>파트너사 기본 정보</h4>
                    <ul className={style["validation-rules"]}>
                        <li>파트너사 아이디는 3자 이상이며 기존 아이디와 중복될 수 없습니다.</li>
                        <li>파트너사 이름은 2자 이상이며 기존 이름과 중복될 수 없습니다.</li>
                    </ul>
                    <div className={style["unit--overlay--inputs"]}>
                        <BlockInput
                            type="text"
                            placeholder="파트너사 아이디"
                            value={partnerId}
                            setValue={setPartnerId}
                            error={partnerIdError}
                        />

                        <BlockInput
                            type="text"
                            placeholder="파트너사 이름"
                            value={partnerName}
                            setValue={setPartnerName}
                            error={partnerNameError}
                        />
                    </div>
                    <h4 className={common["text--overlay"]}>파트너사 관리자 등록</h4>
                    <ul className={style["validation-rules"]}>
                        <li>관리자 이름은 2자 이상이어야 합니다.</li>
                        <li>관리자 이메일은 이메일 형식이어야 하며 기존 관리자 이메일과 중복될 수 없습니다.</li>
                        <li>관리자 비밀번호는 6자 이상이어야 하고, 확인 값과 같아야 합니다.</li>
                    </ul>
                    <div className={style["unit--overlay--inputs"]}>
                        <BlockInput
                            type="text"
                            placeholder="관리자 이름"
                            value={name}
                            setValue={setName}
                            error={nameError}
                        />
                        <BlockInput
                            type="text"
                            placeholder="관리자 이메일"
                            value={email}
                            setValue={setEmail}
                            error={emailError}
                        />
                        <BlockInput
                            type="password"
                            placeholder="관리자 비밀번호"
                            value={password}
                            setValue={setPassword}
                            error={passwordError}
                        />
                        <BlockInput
                            type="password"
                            placeholder="관리자 비밀번호 확인"
                            value={confirm}
                            setValue={setConfirm}
                            error={confirmError}
                        />
                    </div>
                </div>
                <div className={common["wrapper--overlay--footer"]}>
                    <ListButton type="Default" text="취소" onClick={hide} />
                    <ListButton type="Primary" text="등록" onClick={createPartner} loading={loading} />
                </div>
            </div>
        </div>
    );
};

const EditPartner = ({ partner, close }: { partner: PartnerType; close: () => void }) => {
    const [partnerId, setPartnerId] = useState<string>("");
    const [partnerIdError, setPartnerIdError] = useState<string>("");

    useEffect(() => {
        if (partnerId && partnerId.length < 5) {
            setPartnerIdError(ErrMsg["auth/partnerId-invalid"]);
        } else {
            setPartnerIdError("");
        }
    }, [partnerId]);
    const [name, setName] = useState<string>("");
    const [nameError, setNameError] = useState<string>("");

    useEffect(() => {
        if (name && name.length < 2) {
            setNameError(ErrMsg["auth/name-invalid"]);
        } else {
            setNameError("");
        }
    }, [name]);

    const [deleteBtn, setDeleteBtn] = useState<"Delete" | "DeleteFill">("Delete");
    const [loadingDelete, setLoadingDelete] = useState<boolean>(false);

    const deletePartner = () => {
        if (deleteBtn === "Delete") {
            setDeleteBtn("DeleteFill");
            return;
        }

        setLoadingDelete(true);

        return httpsCallable(
            functions,
            "partnerDelete"
        )({
            docId: partner.docId,
        })
            .then((result) => {
                setLoadingDelete(false);
                setDeleteBtn("Delete");

                const data = result.data as {
                    code?: string;
                    message?: string;
                };

                if (data.code) {
                    hide();
                }
            })
            .catch((err) => {
                console.error(err);
                setLoadingDelete(false);
                setDeleteBtn("Delete");

                if (ErrMsg[err.message]) {
                    alert(ErrMsg[err.message]);
                } else {
                    alert("에러가 발생했습니다");
                }

                return;
            });
    };

    const [active, setActive] = useState<boolean>(false);
    useEffect(() => {
        setActive(true);
    }, []);
    const hide = () => {
        setActive(false);
        setTimeout(() => {
            close();
        }, 520);
    };
    return (
        <div className={[common["container--overlay"], active && common["container--overlay--active"]].join(" ")}>
            <h4 className={[common["text--title"], common["text--title--overlay"]].join(" ")}>파트너사 추가</h4>

            <div className={[common["unit--overlay"], active && common["unit--overlay--active"]].join(" ")}>
                <div className={common["wrapper--overlay"]}>
                    <h4 className={common["text--overlay"]}>파트너사 기본 정보</h4>
                    <div className={style["unit--overlay--inputs"]}>
                        <BlockInput
                            type="text"
                            placeholder="파트너사 아이디"
                            value={partner.id}
                            setValue={setPartnerId}
                            error={partnerIdError}
                            disabled={true}
                        />

                        <BlockInput type="text" placeholder="이름" value={name} setValue={setName} error={nameError} />
                    </div>
                    <h4 className={common["text--overlay"]}>파트너사 삭제</h4>
                    <div className={style["unit--overlay--inputs"]}>
                        <BlockButton
                            type={deleteBtn}
                            text={deleteBtn === "Delete" ? "삭제하기" : "삭제 확인하기"}
                            loading={loadingDelete}
                            onClick={deletePartner}
                        />
                    </div>
                </div>
                <div className={common["wrapper--overlay--footer"]}>
                    <ListButton type="Default" text="취소" onClick={hide} />
                </div>
            </div>
        </div>
    );
};

const PartnerTable = () => {
    const { partner } = usePartner();
    const [selected, setSelected] = useState<PartnerType | null>(null);

    return (
        <>
            <div className={style["container--title"]}>
                <p className={style["text--eyebrow"]}>등록된 파트너사</p>
            </div>
            <div className={style["wrapper--partner-list"]}>
                {partner.map((p, i) => (
                    <PartnerList key={`parnter-${i}`} partner={p} setPartner={() => setSelected(p)} />
                ))}
            </div>

            {selected && <EditPartner close={() => setSelected(null)} partner={selected} />}
        </>
    );
};

const PartnerList = ({ partner, setPartner }: { partner: PartnerType; setPartner: () => void }) => {
    return (
        <div className={style["unit--partner-list"]} onClick={setPartner}>
            <p className={style["text--partner--eyebrow"]}>{partner.id}</p>
            <p className={style["text--partner--head"]}>{partner.name}</p>
        </div>
    );
};

export default Partner;
